import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { titleCase } from "@/lib/format";
import { PageHeader, cardWrap, hdrCell, ghostBtn } from "@/components/admin-ui";
import { inputBase } from "@/components/ui-prims";

// Mirrors the /admin/dataengine/reconcile/queue response shape.
type QueueRow = {
  slug: string;
  name: string;
  district?: string | null;
  flags: string[];
  confidence?: number | null;
  last_synced_at: string;
  txn_count_window: number;
  // PROP-82 — active user-report aggregates. Zero / empty when no
  // active reports exist for the slug. The badge below renders only
  // when reports_active_count > 0.
  reports_active_count?: number;
  reports_active_reporters?: number;
  last_reported_at?: string;
};

type QueueResponse = {
  rows: QueueRow[];
  total: number;
  page: number;
  page_size: number;
  flag: string;
  q: string;
  sort: string;
  // Echo of constants.ReconcileTxnWindowDays — drives the column label
  // so changing the const is a single-file edit.
  txn_window_days: number;
};

type FlagOption = {
  value: string;
  label: string;
  blurb: string;
};

const FLAG_OPTIONS: FlagOption[] = [
  { value: "",                   label: "Any flag",          blurb: "Anything the extractor or reconciler flagged" },
  { value: "low_confidence",     label: "Low confidence",    blurb: "Extractor confidence below threshold" },
  { value: "multi_plan_suspect", label: "Multi-plan suspect", blurb: "One image likely covers >1 unit type" },
  { value: "missing_bbox",       label: "Missing bbox",      blurb: "A unit row has no room bboxes" },
  { value: "ura_cos_mismatch",   label: "URA ↔ cos unmatched", blurb: "cos.sg row without a tier 1+2 URA pair" },
  // PROP-82 — surfaces slugs that a real user flagged via the buyer-side
  // report form. Distinct from the four machine signals above: the
  // operator sees something a heuristic didn't catch.
  { value: "user_reported",      label: "User-reported",     blurb: "A buyer flagged this project as inaccurate" },
];

const FLAG_LABEL: Record<string, string> = Object.fromEntries(
  FLAG_OPTIONS.filter((f) => f.value).map((f) => [f.value, f.label]),
);

// Mirror of store.ReconcileQueueSort. Server validates the enum; the
// dropdown just lets the operator pick. The {window}d sort labels are
// templated against the response's txn_window_days so swapping the
// backend constant doesn't leave stale digits in the UI.
const SORT_OPTIONS = (windowDays: number): { value: string; label: string }[] => [
  { value: "confidence",       label: "Low confidence first" },
  { value: "tx_90d_desc",      label: `Most active (${windowDays}d txns ↓)` },
  { value: "tx_90d_asc",       label: `Least active (${windowDays}d txns ↑)` },
  { value: "last_synced_desc", label: "Recently synced" },
];

const SEARCH_DEBOUNCE_MS = 300;

export function ReconcileQueuePage() {
  const navigate = useNavigate();

  const [flag, setFlag] = useState<string>("");
  const [sort, setSort] = useState<string>("confidence");
  const [searchInput, setSearchInput] = useState<string>(""); // raw input
  const [searchQuery, setSearchQuery] = useState<string>(""); // debounced

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [txnWindowDays, setTxnWindowDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Debounce searchInput → searchQuery so we don't refetch per keystroke.
  useEffect(() => {
    const h = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams();
    if (flag) qs.set("flag", flag);
    if (sort && sort !== "confidence") qs.set("sort", sort);
    if (searchQuery) qs.set("q", searchQuery);
    qs.set("page", String(page));
    (async () => {
      try {
        const data = await api.get<QueueResponse>(
          `/admin/dataengine/reconcile/queue?${qs.toString()}`,
        );
        if (!cancelled) {
          setRows(data.rows ?? []);
          setTotal(data.total ?? 0);
          setPageSize(data.page_size ?? 50);
          if (typeof data.txn_window_days === "number") setTxnWindowDays(data.txn_window_days);
          setErr(null);
        }
      } catch (e: any) {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [flag, sort, searchQuery, page]);

  // Active filter pill needs the human label.
  const activeFlagLabel = useMemo(
    () => FLAG_OPTIONS.find((f) => f.value === flag)?.label ?? "Any flag",
    [flag],
  );

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <PageHeader
        kicker="Data engine"
        title="Data Studio"
        sub="Projects the floorplan extractor or URA↔cos reconciler flagged as worth a human pass — or search to jump to any project."
      />

      {/* Search + sort row */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, margin: "0 26px 12px", alignItems: "center" }}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by slug, URA name, or cos title…"
          style={{ ...inputBase, flex: "1 1 320px", fontSize: 12 }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--ink-3)" }}>
          <span>Sort</span>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            style={{ ...inputBase, fontSize: 12, padding: "5px 8px" }}
          >
            {SORT_OPTIONS(txnWindowDays).map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Filter chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "0 26px 14px" }}>
        {FLAG_OPTIONS.map((f) => {
          const active = f.value === flag;
          return (
            <button
              key={f.value || "any"}
              type="button"
              title={f.blurb}
              onClick={() => { setFlag(f.value); setPage(1); }}
              style={{
                ...ghostBtn,
                padding: "5px 10px", fontSize: 11.5,
                background: active ? "var(--accent-soft)" : "var(--surface)",
                color: active ? "var(--accent-ink, #0e2c22)" : "var(--ink-2)",
                fontWeight: active ? 600 : 500,
              }}
            >
              {f.label}
            </button>
          );
        })}
        {searchQuery && (
          <span style={{ fontSize: 11, color: "var(--ink-4)", fontStyle: "italic", alignSelf: "center" }}>
            search results include unflagged projects
          </span>
        )}
      </div>

      {err && (
        <div style={{
          margin: "0 26px 14px", background: "rgba(122,45,31,0.08)", color: "var(--neg)",
          padding: "10px 14px", borderRadius: 8, fontSize: 13,
        }}>
          {err}
        </div>
      )}

      <div style={cardWrap}>
        <div style={hdrRow}>
          <div style={hdrCell}>Project</div>
          <div style={hdrCell}>District</div>
          <div style={hdrCell}>Flags</div>
          <div style={{ ...hdrCell, textAlign: "right" }} title={`Transactions in the last ${txnWindowDays} days`}>{txnWindowDays}d tx</div>
          <div style={{ ...hdrCell, textAlign: "right" }}>Confidence</div>
          <div style={{ ...hdrCell, textAlign: "right" }}>Last synced</div>
        </div>

        {loading && rows.length === 0 && (
          <div style={emptyCell}>Loading…</div>
        )}
        {!loading && rows.length === 0 && !err && (
          <div style={emptyCell}>
            {searchQuery
              ? `No projects match "${searchQuery}".`
              : "No projects match this filter."}
          </div>
        )}

        {rows.map((r) => (
          <div
            key={r.slug}
            className="ops-row"
            style={dataRow}
            onClick={() => navigate(`/dataengine/reconcile/${encodeURIComponent(r.slug)}`)}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: "var(--ink)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {titleCase(r.name)}
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.slug}
              </div>
              {/* PROP-82: per-slug active-report badge. Hidden when
                  zero so the row stays calm for clean / machine-only
                  flagged projects. */}
              {(r.reports_active_count ?? 0) > 0 && (
                <div
                  title={r.last_reported_at ? `Last reported ${fmtTime(r.last_reported_at)}` : undefined}
                  style={{
                    marginTop: 3,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 11,
                    color: "var(--ag-amber, #b45309)",
                    fontWeight: 500,
                  }}
                >
                  <span aria-hidden>🚩</span>
                  <span aria-label={`${r.reports_active_count} active report${r.reports_active_count === 1 ? "" : "s"} from ${r.reports_active_reporters} reporter${r.reports_active_reporters === 1 ? "" : "s"}`}>
                    {r.reports_active_count} report{r.reports_active_count === 1 ? "" : "s"}
                    {(r.reports_active_reporters ?? 0) > 1 && ` · ${r.reports_active_reporters} reporters`}
                  </span>
                </div>
              )}
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-2)" }}>
              {r.district ? `D${r.district}` : "—"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {(r.flags ?? []).length === 0
                ? <span style={{ fontSize: 11, color: "var(--ink-4)", fontStyle: "italic" }}>—</span>
                : (r.flags ?? []).map((f) => (
                    <span key={f} style={flagPill(f === flag)}>{FLAG_LABEL[f] ?? f}</span>
                  ))
              }
            </div>
            <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12, color: r.txn_count_window > 0 ? "var(--ink-2)" : "var(--ink-4)" }}>
              {r.txn_count_window}
            </div>
            <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-2)" }}>
              {r.confidence == null ? "—" : r.confidence.toFixed(2)}
            </div>
            <div style={{ textAlign: "right", fontSize: 11.5, color: "var(--ink-3)" }}>
              {fmtTime(r.last_synced_at)}
            </div>
          </div>
        ))}
      </div>

      {/* Pager */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        margin: "12px 26px 22px", fontSize: 12, color: "var(--ink-3)",
      }}>
        <div>
          {total.toLocaleString("en-SG")} {searchQuery ? "matches" : "flagged"} · filter: <strong style={{ color: "var(--ink-2)" }}>{activeFlagLabel}</strong>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{ ...ghostBtn, padding: "5px 10px", fontSize: 11.5, opacity: page <= 1 ? 0.5 : 1 }}
          >
            ← Prev
          </button>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{ ...ghostBtn, padding: "5px 10px", fontSize: 11.5, opacity: page >= totalPages ? 0.5 : 1 }}
          >
            Next →
          </button>
        </div>
      </div>
    </>
  );
}

function fmtTime(s?: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-SG", { dateStyle: "short", timeStyle: "short" });
}

// Project | District | Flags | {window}d tx | Confidence | Last synced
const gridCols = "1.5fr 70px 2fr 80px 90px 140px";

const hdrRow: React.CSSProperties = {
  display: "grid", gridTemplateColumns: gridCols, gap: 12,
  padding: "10px 14px", background: "var(--surface-2)",
  borderBottom: "1px solid var(--rule)",
};
const dataRow: React.CSSProperties = {
  display: "grid", gridTemplateColumns: gridCols, gap: 12,
  padding: "10px 14px", alignItems: "center",
  borderBottom: "1px solid var(--rule)",
};
const emptyCell: React.CSSProperties = {
  padding: 24, textAlign: "center", color: "var(--ink-3)", fontStyle: "italic",
};

function flagPill(active: boolean): React.CSSProperties {
  return {
    display: "inline-flex", alignItems: "center",
    padding: "2px 7px", borderRadius: 4,
    background: active ? "var(--accent-soft)" : "var(--surface-2)",
    color: active ? "var(--accent-ink, #0e2c22)" : "var(--ink-2)",
    fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600,
    letterSpacing: 0.04, textTransform: "uppercase",
  };
}
